import { useState, useEffect } from "react";
import axios from "axios";
import { Form, Modal, Button, Table } from "react-bootstrap";
import swal from "sweetalert";
import { useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../../assets/global/url";
import productUnits from "../../../assets/global/unitofmeasure";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../../assets/img/NoAccess.png";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import Select from "react-select";
import { selectCustomStyles } from "../../../assets/global/selectCustomStyles";
import { constant_productCategory } from "../../../constants/productOptions";
import imageCompression from "browser-image-compression";
import NumberInDecimals from "../../../components/inputs/NumberFormatted";
import { parseNumber } from "../../../utils/numberFormatter";

const CreateProduct = ({ authrztn }) => {
  const userLoggedID = useDecodeToken();
  const navigate = useNavigate();
  const [validated, setValidated] = useState(false);
  const [vendorData, setVendorData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [images, setImages] = useState([]);
  const MAX_IMAGE_SIZE_MB = 5;
  const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const reloadProductCode = () => {
    axios
      .get(BASE_URL + "/product/lastCode")
      .then((res) => {
        const codes =
          res.data !== null ? res.data.toString().padStart(6, "0") : "000001";
        setValue("productCode", codes);
        setIsLoading(false);
      })
      .catch((err) => console.log(err));
  };

  const fetchVendorData = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/product/getVendors`);
      setVendorData(res.data);
      // setVendorData([]);
    } catch (error) {
      console.error(error);
    }
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const compressedImages = [];
    for (const file of files) {
      // Compress image
      try {
        const options = {
          maxSizeMB: 5, // keep under 5MB
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);
        if (compressedFile.size > MAX_IMAGE_SIZE_BYTES) {
          swal({
            icon: "error",
            title: "File Too Large",
            text: `The file "${file.name}" exceeds the ${MAX_IMAGE_SIZE_MB} MB limit even after compression.`,
          });
          return;
        }
        compressedImages.push(compressedFile);
      } catch (err) {
        swal({
          icon: "error",
          title: "Compression Error",
          text: `Failed to compress "${file.name}".`,
        });
        return;
      }
    }
    setImages(compressedImages);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      reloadProductCode();
      fetchVendorData();
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const { register, control, setValue, getValues, watch } = useForm({
    defaultValues: {
      tagAllVendor: false,
      productCode: "",
      productName: "",
      productCategory: "",
      productUnit: "",
      remarks: "",
      productThreshold: "",
      items: [
        {
          companyName: "",
          vendorId: "",
          vendorEmail: "",
          vendoryCountry: "",
          vendorPrice: 0,
        },
      ],
    },
  });

  const {
    fields: vendortable,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "items",
  });

  const itemsValues = useWatch({
    control,
    name: "items",
    defaultValue: [],
  });

  // Open modal at selected image
  const handleViewImage = (index) => {
    setCurrentImageIndex(index);
    setShowImageModal(true);
  };

  // Next/Prev handlers
  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const addNewItem = () => {
    const tagAllVendor = getValues("tagAllVendor");
    const currentItems = getValues("items") || [];

    if (
      !tagAllVendor &&
      currentItems.length > 0 &&
      currentItems.some((item) => !item.vendorId)
    ) {
      swal({
        icon: "warning",
        title: "Supplier Required",
        text: "Please select a supplier for the existing rows before adding a new item.",
      });
      return;
    }

    append({
      companyName: "",
      vendorId: "",
      vendorEmail: "",
      vendoryCountry: "",
      // invoice_date: { [Op.between]: [thisFromdate, thisTodate] },
      vendorPrice: 0,
    });
  };

  const add = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in all the required text fields.",
      });
      return;
    }

    const tagAllVendor = getValues("tagAllVendor");
    const currentItems = getValues("items") || [];

    // Supplier Required Validation
    if (
      !tagAllVendor &&
      (currentItems.length === 0 ||
        currentItems.some((item) => !item.companyName))
    ) {
      swal({
        icon: "warning",
        title: "Supplier Required",
        text: "Please select a supplier or check 'Tag All Supplier' before continuing.",
      });
      return;
    }

    // Check for zero prices
    const hasZeroPrice = currentItems.some(
      (item) => parseNumber(item.vendorPrice) === 0 || !item.vendorPrice,
    );

    if (hasZeroPrice) {
      await swal({
        icon: "info",
        title: "Price Adjusted",
        text: "Any supplier price set to 0 will automatically be adjusted to 1.",
      });
    }

    swal({
      title: "Create this new product?",
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        const formData = getValues();

        const formattedFormData = {
          ...formData,
          productThreshold: parseNumber(formData.productThreshold),
          items: formData.items.map((item) => {
            let pPrice = parseNumber(item.vendorPrice) || 0;
            if (pPrice === 0) pPrice = 1;

            return {
              ...item,
              vendorPrice: pPrice,
            };
          }),
          userLoggedID,
        };

        const data = new FormData();
        Object.entries(formattedFormData).forEach(([key, value]) => {
          if (key === "items") {
            data.append(key, JSON.stringify(value));
          } else {
            data.append(key, value);
          }
        });
        images.forEach((img) => data.append("images", img));

        axios
          .post(`${BASE_URL}/product/createProduct`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Success",
                text: "Product created successfully",
                icon: "success",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              }).then(() => {
                navigate("/inventory/product-list");
              });
            } else {
              swal({
                title: "Something Went Wrong",
                text: "Please contact your support immediately",
                icon: "error",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              });
            }
          })
          .catch((error) => {
            // Handle Product Name Already Exists Error
            if (error.response && error.response.status === 409) {
              const wrapper = document.createElement("div");
              wrapper.classList.add("center-swal-text");
              wrapper.innerHTML =
                "Please choose a unique name for the product to proceed.";
              swal({
                icon: "error",
                title: error.response.data.message,
                content: wrapper,
                button: "OK",
              });
              return;
            }
          });
      }
    });

    setValidated(true);
  };

  return (
    <>
      <div className="h-100 w-100 border bg-white custom-container">
        {isLoading ? (
          <div className="loading-container">
            <ThreeDot
              variant="brick-stack"
              color="#6290FE"
              size="large"
              text="Loading Data..."
              textColor=""
            />
          </div>
        ) : authrztn.includes("ProductList-Add") ? (
          <>
            <Form noValidate validated={validated} onSubmit={add}>
              <div className="w-100 p-2 d-flex flex-row justify-content-between">
                <div className="d-flex flex-column title-custom">
                  <span className="fs-3">
                    <Link
                      to="/inventory/product-list"
                      className="text-dark me-2"
                    >
                      <i className="fa-solid fa-arrow-left"></i>
                    </Link>
                    CREATE PRODUCT
                  </span>
                </div>
              </div>

              <div className="p-2">
                <span className="text-decoration-underline">
                  GENERAL INFORMATION
                </span>
                <div className="row mb-3 mt-3">
                  <div className="col-md-6">
                    <Form.Label htmlFor="productCode">Product Code</Form.Label>
                    <Form.Control
                      type="text"
                      className="p-3"
                      id="productCode"
                      {...register("productCode")}
                      readOnly
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Label htmlFor="productName">
                      Product Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      className="p-3"
                      placeholder="Enter Item Name"
                      id="productName"
                      {...register("productName")}
                      required
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Label htmlFor="productUnit">
                      Unit of Measure <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      className="form-select p-3"
                      id="productUnit"
                      {...register("productUnit")}
                      required
                    >
                      <option disabled value="">
                        Select Unit
                      </option>
                      {productUnits.map((unit, index) => (
                        <option key={index} value={unit.value}>
                          {unit.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-md-6">
                    <Form.Label htmlFor="productCategory">
                      Product Category <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      className="p-3"
                      id="productCategory"
                      {...register("productCategory")}
                      required
                    >
                      <option disabled value="">
                        Select Category
                      </option>
                      {constant_productCategory.map((product, index) => (
                        <option key={index} value={product}>
                          {product}
                        </option>
                      ))}
                      {/* <option value="Consumable">Consumable</option>
                         <option value="Fixed Asset">Fixed Asset</option> */}
                    </Form.Select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Label htmlFor="productImages">
                      Product Images
                    </Form.Label>
                    <Form.Control
                      type="file"
                      id="productImages"
                      multiple
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <div className="mt-2">
                      {images.length > 0 &&
                        Array.from(images).map((img, idx) => (
                          <span key={idx} className="badge bg-info me-1">
                            {img.name}
                          </span>
                        ))}
                    </div>
                    {/* VIEW BUTTON */}
                    {images.length > 0 && (
                      <Button
                        variant="info"
                        size="sm"
                        className="mt-2"
                        onClick={() => handleViewImage(0)}
                      >
                        View All Images
                      </Button>
                    )}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Label htmlFor="remarks">Remarks</Form.Label>
                    <Form.Control
                      id="remarks"
                      as="textarea"
                      rows={3}
                      style={{
                        fontSize: "16px",
                        height: "200px",
                        maxHeight: "200px",
                        resize: "none",
                        overflowY: "auto",
                      }}
                      placeholder="Enter Remarks"
                      {...register("remarks")}
                    />
                  </div>
                </div>

                <span className="text-decoration-underline">
                  THRESHOLD NOTIFICATION
                </span>
                <div className="row mb-3 mt-3">
                  <div className="col-md-6">
                    <Form.Label htmlFor="productThreshold">
                      Critical Inventory Threshold{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="input-group">
                      <span className="input-group-text p-3">No.</span>
                      <Controller
                        name="productThreshold"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <NumberInDecimals
                            className="form-control p-3"
                            placeholder="0.00"
                            decimals={2}
                            value={field.value}
                            onChange={field.onChange}
                            required
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <Form.Group className="form-check form-switch d-flex gap-2 justify-content-end align-items-end h-100">
                      <input
                        type="checkbox"
                        className="form-check-input p-2 mb-1"
                        id="tag-all-vendor"
                        {...register("tagAllVendor")}
                        role="switch"
                      />

                      <Form.Label htmlFor="tag-all-vendor" className="mb-0">
                        Tag All Supplier
                      </Form.Label>
                    </Form.Group>
                  </div>
                </div>
              </div>

              <Table bordered hover responsive>
                <thead className="table-light">
                  <tr>
                    <th className="p-2">
                      Company Name <span className="text-danger">*</span>
                    </th>
                    <th className="p-2">Contact Person</th>
                    <th className="p-2">Company Email</th>
                    <th className="p-2">Country</th>
                    <th className="p-2">
                      Price <span className="text-danger">*</span>
                    </th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {vendortable.map((item, index) => (
                    <tr key={item.id}>
                      <td>
                        <Controller
                          name={`items.${index}.vendorId`}
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => {
                            const vendorOptions = (
                              vendortable.length === 1
                                ? vendorData
                                : vendorData.filter(
                                    (data) =>
                                      !selectedVendors.includes(data.id) ||
                                      selectedVendors[index] === data.id,
                                  )
                            ).map((data) => ({
                              value: data.id,
                              label:
                                data.company_name ||
                                `${data.fname} ${data.lname}`,
                            }));

                            return (
                              <Select
                                {...field}
                                options={vendorOptions}
                                value={
                                  vendorOptions.find(
                                    (opt) => opt.value === field.value,
                                  ) || null
                                }
                                onChange={(selectedOption) => {
                                  field.onChange(selectedOption?.value);
                                  const selectedValue = String(
                                    selectedOption.value,
                                    10,
                                  );
                                  const selectedVendor = vendorData.find(
                                    (p) => String(p.id) === selectedValue,
                                  );

                                  if (selectedVendor) {
                                    setValue(
                                      `items.${index}.companyName`,
                                      selectedVendor.company_name,
                                    );
                                    setValue(
                                      `items.${index}.vendorName`,
                                      selectedVendor.fname == null ||
                                        selectedVendor.lname == null
                                        ? selectedVendor.company_name
                                        : `${selectedVendor.fname} ${selectedVendor.lname}`,
                                    );
                                    setValue(
                                      `items.${index}.vendorEmail`,
                                      selectedVendor.company_email,
                                    );
                                    setValue(
                                      `items.${index}.vendorCountry`,
                                      selectedVendor.company_country,
                                    );

                                    setSelectedVendors((prev) => {
                                      const updated = [...prev];
                                      updated[index] = selectedValue;
                                      return updated;
                                    });
                                  }
                                }}
                                onMenuOpen={() => {
                                  if (
                                    vendorData.length === 0 &&
                                    selectedVendors.length === 0
                                  ) {
                                    swal({
                                      icon: "warning",
                                      title: "No Supplier Found",
                                      text: "No suppliers found. Please create one first.",
                                    });
                                    return;
                                  }
                                }}
                                menuIsOpen={
                                  vendorData.length === 0 &&
                                  selectedVendors.length === 0
                                    ? false
                                    : undefined
                                }
                                menuPortalTarget={document.body}
                                placeholder="Select Supplier"
                                styles={selectCustomStyles(
                                  watch("tagAllVendor") ||
                                    watch(`items.${index}.vendorId`),
                                  validated,
                                  "0.12rem",
                                )}
                                isSearchable
                              />
                            );
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          {...register(`items.${index}.vendorName`)}
                          className="p-2"
                          readOnly
                        />
                      </td>
                      <td>
                        <Form.Control
                          {...register(`items.${index}.vendorEmail`)}
                          className="p-2"
                          readOnly
                        />
                      </td>
                      <td>
                        <Form.Control
                          {...register(`items.${index}.vendorCountry`)}
                          className="p-2"
                          readOnly
                        />
                      </td>
                      <td>
                        <Controller
                          name={`items.${index}.vendorPrice`}
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <NumberInDecimals
                              className="form-control p-2"
                              placeholder="0.00"
                              value={field.value}
                              onChange={field.onChange}
                              decimals={4}
                              required
                            />
                          )}
                        />
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            remove(index);
                            setSelectedVendors((prev) =>
                              prev.filter((_, i) => i !== index),
                            );
                          }}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="text-end mt-2">
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={addNewItem}
                >
                  New Item
                </button>
              </div>
              <div className="row mx-auto p-2 mt-4 mb-4"></div>
              <div className="w-100 text-end mb-3 ">
                <button
                  type="button"
                  className="btn btn-secondary title-button mx-3"
                  onClick={() => navigate("/inventory/product-list")}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary title-button">
                  Save
                </button>
              </div>
            </Form>
          </>
        ) : (
          <div className="no-access">
            <img src={NoAccess} alt="NoAccess" className="no-access-img" />
            <h3>You don't have access to this function.</h3>
          </div>
        )}

        <Modal
          show={showImageModal}
          onHide={() => setShowImageModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Image {currentImageIndex + 1} of {images.length}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-flex flex-column align-items-center">
            {images.length > 0 && (
              <img
                src={URL.createObjectURL(images[currentImageIndex])}
                alt={images[currentImageIndex].name}
                style={{ maxWidth: "100%", maxHeight: "60vh" }}
              />
            )}
            <div className="mt-3 d-flex justify-content-between w-100">
              <Button
                variant="secondary"
                onClick={handlePrevImage}
                disabled={images.length <= 1}
              >
                &lt;
              </Button>
              <Button
                variant="secondary"
                onClick={handleNextImage}
                disabled={images.length <= 1}
              >
                &gt;
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default CreateProduct;
